import ConversationModel from 'Models/ConversationModel';
import { ConversationsListSlice } from 'Modules/conversation/conversations-list/store/index.types';
import {
  moveConversationBetweenLists,
  removeDuplicateConversations,
  removePinnedConversations,
  sortAndRemoveDuplicatesConversations,
} from 'Modules/conversation/utils/conversationsListManagement';
import { StateCreator } from 'zustand';
import {
  PinnedConversationsSliceState,
  PinnedConversationsSlice,
} from './index.types';

export const initialState: PinnedConversationsSliceState = {
  pinnedConversations: [],
  isPinningOrUnpinning: false,
};

export const createPinnedConversationsSlice: StateCreator<
  PinnedConversationsSlice & Partial<ConversationsListSlice>
> = (set, get) => ({
  ...initialState,
  setPinnedConversations: (pinnedConversations) => {
    // Update pinned conversations and ensure regular list does not contain pinned ones
    set((state) => ({
      pinnedConversations: removeDuplicateConversations(pinnedConversations),
      conversationsList: sortAndRemoveDuplicatesConversations(
        removePinnedConversations(state.conversationsList, pinnedConversations)
      ),
    }));
  },
  setIsConversationPinned: (conversationId, pinned) => {
    // If conversation is pinned, move it to the pinned conversations list and remove it from the conversations list
    if (pinned) {
      const movedConversations = moveConversationBetweenLists(
        get().conversationsList,
        get().pinnedConversations,
        conversationId
      );

      if (movedConversations) {
        set({
          conversationsList: sortAndRemoveDuplicatesConversations(
            removePinnedConversations(
              movedConversations.from,
              movedConversations.to
            )
          ),
          pinnedConversations: removeDuplicateConversations(
            movedConversations.to
          ),
        });

        return true;
      }

      return false;
    }

    // If conversation is unpinned, move it to the conversations list and remove it from the pinned conversations list
    const movedConversations = moveConversationBetweenLists(
      get().pinnedConversations,
      get().conversationsList,
      conversationId
    );

    if (movedConversations) {
      set({
        conversationsList: sortAndRemoveDuplicatesConversations(
          removePinnedConversations(
            movedConversations.to,
            movedConversations.from
          )
        ),
        pinnedConversations: removeDuplicateConversations(
          movedConversations.from
        ),
      });

      return true;
    }

    return false;
  },
  updatePinnedConversation: (updatedConversation) => {
    // Check if conversation exists in pinned conversations
    const pinnedConversationIndex = get().pinnedConversations.findIndex(
      (conversation) => conversation.id === updatedConversation.id
    );

    if (pinnedConversationIndex !== -1) {
      // Update pinned conversation
      const updatedPinnedConversations = [...get().pinnedConversations];
      updatedPinnedConversations.splice(
        pinnedConversationIndex,
        1,
        updatedConversation
      );

      set({
        pinnedConversations: removeDuplicateConversations(
          updatedPinnedConversations
        ),
      });

      return true;
    }

    return false;
  },
  updatePinnedConversationFields: (
    conversationId,
    updatedConversationFields
  ) => {
    // Update in pinned conversations
    const pinnedConversationIndex = get().pinnedConversations.findIndex(
      (conversation) => conversation.id === conversationId
    );

    if (pinnedConversationIndex !== -1) {
      const updatedPinnedConversations = [...get().pinnedConversations];

      updatedPinnedConversations.splice(pinnedConversationIndex, 1, {
        ...updatedPinnedConversations[pinnedConversationIndex],
        ...updatedConversationFields,
      } as ConversationModel);

      set({
        pinnedConversations: removeDuplicateConversations(
          updatedPinnedConversations
        ),
      });

      return true;
    }

    return false;
  },
  removePinnedConversation: (conversationId) => {
    const updatedPinnedConversations = get().pinnedConversations.filter(
      (conversation) => conversation.id !== conversationId
    );

    if (
      updatedPinnedConversations.length !== get().pinnedConversations.length
    ) {
      set({
        pinnedConversations: removeDuplicateConversations(
          updatedPinnedConversations
        ),
      });

      return true;
    }

    return false;
  },
  getIsConversationPinned: (conversationId) =>
    get().pinnedConversations.some(
      (conversation) => conversation.id === conversationId
    ),
  setIsPinningOrUnpinning: (isPinningOrUnpinning) => {
    set({ isPinningOrUnpinning });
  },
});
